import React, { useState } from 'react';
import _omit from 'lodash/omit';
import Tokens from '@nokia-csf-uxr/freeform-design-tokens';
import { getTextWidth, getContext } from 'common';
import { TextwChipsList } from 'components/TextwChips';
import { MenuProps } from 'components/Menu';

import Styled from './styles';

const {
  FONT_SIZE,
  FONT_FAMILY,
} = Tokens;
const INPUT_PADDING = 1;
const TEXT_FONT = `${FONT_SIZE.FONT_SIZE_14} ${FONT_FAMILY.NOKIA_PURE_TEXT}`;

export interface ListProps {
  event?: {},
  items: {
    label?: string,
    itemNode?: React.ReactNode,
    key?: string,
    content?: React.ReactNode,
    searchPattern?: string,
    onClick: React.MouseEventHandler,
    submitChip?: boolean
  }[]
}

export interface TextwChipsHighlighterProps extends React.ComponentPropsWithRef<'input'> {
  /** The value of the field. */
  value: string;
  /** Set to true, if list is focused. */
  listFocused?: boolean;
  /** Callback fired when the value is changed. */
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  /** Properties for TextwChipsList. */
  listProps?: {
    triggerEl?: React.RefObject<HTMLElement>;
    listRef?: React.RefObject<HTMLElement>;
    onClose?: () => any;
    autocomplete?: {
      event?: {},
      items: {
        label?: string,
        itemNode?: React.ReactNode,
        key?: string,
        content?: React.ReactNode,
        searchPattern?: string,
        onClick?: React.MouseEventHandler,
        submitChip?: boolean
      }[]
    },
    menuProps?: Partial<MenuProps>;
  };
  /** @ignore */
  onFocus?: React.FocusEventHandler;
  /** @ignore */
  onBlur?: React.FocusEventHandler;
}

/**
 *  Internal component. Any other props than the `TextwChipsHighlighter` props will be provided to the root input element.
 *  Used to color the background of text to indicate a Chip is being created.
 */
const TextwChipsHighlighter = React.forwardRef<HTMLElement, TextwChipsHighlighterProps>((props: TextwChipsHighlighterProps, ref) => getContext(({ RTL }) => {
  const {
    value,
    onChange,
    onFocus,
    onBlur,
    listProps,
    listFocused,
    ...otherProps
  } = props;
  const {
    autocomplete,
    triggerEl,
    listRef,
    onClose,
    menuProps,
    ...otherListProps
  } = listProps || {};
  const [focused, setFocused] = useState(false);

  const handleOnFocus = (event) => {
    setFocused(true);
    onFocus && onFocus(event);
  };
  const handleOnBlur = (event) => {
    setFocused(false);
    onBlur && onBlur(event);
  };
  return (
    <>
      <Styled.ChipEditWrapper
        ref={ref}
        RTL={RTL}
        type="text"
        autoComplete="off"
        onChange={(e) => { onChange && onChange(e); }}
        value={value}
        style={{ width: `${getTextWidth(value, TEXT_FONT) + INPUT_PADDING}rem` }}
        {..._omit(otherProps, ['listFocused', 'listProps'])}
        onFocus={handleOnFocus}
        onBlur={handleOnBlur}
      />
      {((focused || listFocused)) && (
        <TextwChipsList
          onClose={() => { onClose && onClose(); }}
          {...{
            list: autocomplete,
            triggerEl,
            listRef
          }}
          menuProps={menuProps}
          {...otherListProps}
        />
      )
      }
    </>
  );
}));

TextwChipsHighlighter.defaultProps = {
  onChange: undefined,
  listProps: undefined,
  listFocused: false,
  onBlur: undefined,
  onFocus: undefined,
};

TextwChipsHighlighter.displayName = 'TextwChipsHighlighter';
export default TextwChipsHighlighter;
